import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ApiService } from '../../Services/api-service';
import { TranslatePipe } from '@ngx-translate/core';
import { ApiResponse } from '../../models/api-response';
import { LocalizationService } from '../../Services/localization-service';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
interface BlogPost {
  id: string;
  title: string;
  summary?: string;
  content?: string;
  createdAt?: string;
  languageId?: string;
  description?: string;
  keywords?: string;
  imageUrl?: string;
  author: string;
  canonicalUrl: string;
  logoUrl: string;
  keywordArray: string[];
}

@Component({
  selector: 'app-post-component',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    RouterModule
  ],
  templateUrl: './post-component.html',
  styleUrl: './post-component.scss',
})
export class PostComponent implements OnInit {
  private readonly apiService = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly localizationService = inject(LocalizationService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);

  post: BlogPost = {} as BlogPost;
  isLoading = false;
  currentLang = environment.defaultLang;


  ngOnInit(): void {
    this.localizationService.getLanguage().subscribe(lang => {
      if (lang != '') {
        this.currentLang = lang;
        this.cdr.detectChanges();
      }
    });
    this.route.paramMap.subscribe((params) => {
      const slug = params.get('slug');
      if (!slug) {
        return;
      }
      this.loadPost(slug);
    });
  }

  loadPost(slug: string): void {
    this.isLoading = true;
    this.apiService.getData(`api/v1/Blog/GetBlog/${slug}`).subscribe({
      next: (response: ApiResponse) => {
        this.post = response?.data ?? {} as BlogPost;
        this.isLoading = false;
        this.post.keywordArray = this.post.keywords?.split(',') ?? [];
        this.cdr.detectChanges();
        this.updatePostForSEO(this.post, this.currentLang);
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }


  updatePostForSEO(model: BlogPost, lang: string) {
    const currentUrl = `${window.location.origin}/${lang}/post/${model.canonicalUrl}`;
    this.title.setTitle(model.title + ' | AlfaServices');
    const description = this.buildDescription(model);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index,follow' });
    this.meta.updateTag({ name: 'keywords', content: model.keywords || '' });
    this.meta.updateTag({ property: 'og:title', content: model.title });
    this.meta.updateTag({ property: 'og:type', content: 'article' });
    this.meta.updateTag({ property: 'og:description', content: model.description || '' });
    this.meta.updateTag({ property: 'og:url', content: currentUrl });
    this.meta.updateTag({ property: 'og:site_name', content: 'AlfaServices' });
    this.meta.updateTag({ property: 'og:image', content: model.imageUrl || '' });

    this.setCanonical(currentUrl);
    this.setHreflang(lang);
    this.injectStructuredData(model, lang);
  }



  private buildDescription(model: BlogPost): string {
    const parts: string[] = [];
    if (model.description) parts.push(model.description);
    const raw = parts.join(' ').replace(/\s+/g, ' ').trim();
    return raw.length > 155 ? raw.substring(0, 152) + '…' : raw;
  }

  private setCanonical(canonicalUrl: string) {
    this.replaceLinkTag('canonical', canonicalUrl);
  }

  private setHreflang(lang: string) {
    // remove existing hreflang tags
    const existing = document.head.querySelectorAll('link[rel="alternate"][hreflang]');
    existing.forEach(el => el.parentElement?.removeChild(el));
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang.toLowerCase());
    link.setAttribute('href', `${window.location.origin}/${lang}`);
    document.head.appendChild(link);
    // x-default
    const xdef = document.createElement('link');
    xdef.setAttribute('rel', 'alternate');
    xdef.setAttribute('hreflang', 'x-default');
    xdef.setAttribute('href', `${window.location.origin}/${environment.defaultLang}`);
    document.head.appendChild(xdef);
  }

  private injectStructuredData(model: BlogPost, lang: string) {
    const scriptId = 'ld-json-post';
    const old = document.getElementById(scriptId);
    if (old) old.remove();
    const data: any =
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": model.title,
      "image": model.imageUrl,
      "author": {
        "@type": "Organization",
        "name": "AlfaServices"
      },
      "publisher": {
        "@type": "Organization",
        "name": model.author,
        "logo": {
          "@type": "ImageObject",
          "url": model.logoUrl
        }
      },
      "datePublished": model.createdAt,
      "description": model.description

    };
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  }

  private replaceLinkTag(rel: string, href: string) {
    let link = document.head.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', rel);
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
